import React, { useEffect, useRef, useState } from 'react';
import { CloseButton } from './CloseButton';

export function Modal({
  isOpen,
  onClose = () => {},
  title = '',
  headerImg,
  children,
  FooterComponent,
  noPadding,
  noCloseButton = false,
  contentClassName = '',
  noTitleClassName = '',
  autoHeight = false,
}) {
  const modalRef = useRef(null);
  const footerRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  // Update viewport height when it changes
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    // Initial set
    handleResize();

    // Add event listeners for resize and orientation change
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // For mobile browsers where address bar can slide up/down
    window.addEventListener('scroll', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('scroll', handleResize);
    };
  }, []);

  useEffect(() => {
    if (footerRef.current) {
      setFooterHeight(footerRef.current.getBoundingClientRect().height);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (isOpen && event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleOutsideClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Calculate max height based on current viewport height
  const maxModalHeight = viewportHeight * 0.97; // Use 97% of viewport height
  const contentMaxHeight = FooterComponent
    ? `${maxModalHeight - (footerHeight || 0) - 16}px` // Subtract footer height and some padding
    : 'calc(100vh - 50px)';

  return (
    <div className={`fixed inset-0 z-[200] flex ${autoHeight ? 'items-start overflow-y-auto' : 'items-center'} justify-center p-4 animate-fadeIn`}
      style={autoHeight ? { paddingTop: 32, paddingBottom: 32 } : {}}
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 animate-fadeIn"
        aria-hidden="true"
        onClick={handleOutsideClick}
      />

      {/* Modal Content */}
      <div
        ref={modalRef}
        className={`py-2 relative w-full max-w-2xl animate-fadeIn ${contentClassName} h-auto`}
        style={{ maxHeight: autoHeight ? 'none' : `${maxModalHeight}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-2xl shadow-xl h-full flex flex-col">
          <div
            className={`${autoHeight ? 'overflow-y-visible' : 'overflow-y-auto'} flex-grow relative`}
            style={{
              maxHeight: autoHeight ? 'none' : `${contentMaxHeight}`,
              borderRadius: 'inherit',
              maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1), rgba(0,0,0,1))',
            }}
          >
            {/* Header */}
            {title && !headerImg ? (
              <h2 className="text-lg font-semibold text-gray-900 p-4">{title}</h2>
            ) : (!title && headerImg) || (title && headerImg) ? (
              <div className="relative overflow-hidden">
                <img src={headerImg} alt="item header" className="w-full h-[240px] object-cover" />
              </div>
            ) : null}

            {!noCloseButton && (
              <CloseButton
                className="absolute top-[25px] right-[16px] z-50 animate-fadeIn"
                onClick={onClose}
              />
            )}

            <div
              className={`${!noPadding ? 'p-4 pt-0' : ''}${
                !title
                  ? noTitleClassName
                    ? ` ${noTitleClassName}`
                    : ' mt-4 sm:mt-8 md:mt-12 lg:mt-16'
                  : ''
              }`}
            >
              {children}
            </div>
          </div>

          {FooterComponent && (
            <div ref={footerRef} className="flex-shrink-0">
              <FooterComponent />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
